import { useEffect, useState, useMemo, Fragment } from "react";
import { api, formatApiError } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Save, Trash2, Trophy, User, ChevronDown, ChevronRight, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { toAuthUrl } from "@/components/PhotoUpload";
import { Link } from "react-router-dom";

const BASIC_COLS = [
  { key: "minutes_played", label: "Min" },
  { key: "points", label: "Pts" },
  { key: "assists", label: "Pas" },
  { key: "rebounds", label: "Reb" },
  { key: "steals", label: "Int" },
  { key: "blocks", label: "Cnt" },
  { key: "fouls", label: "Fts" },
  { key: "turnovers", label: "BP" },
];

const STATUS_OPTIONS = [
  { value: "titulaire", label: "Titulaire", color: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  { value: "remplacant", label: "Remplaçante", color: "bg-blue-100 text-blue-700 border-blue-200" },
  { value: "absent", label: "Absente", color: "bg-slate-100 text-slate-600 border-slate-200" },
  { value: "blesse", label: "Blessée", color: "bg-red-100 text-red-700 border-red-200" },
];

const DEFAULT_DRAFT = {
  status: "titulaire",
  minutes_played: 0, points: 0, assists: 0, rebounds: 0, steals: 0,
  blocks: 0, fouls: 0, turnovers: 0,
  fg_made: 0, fg_attempted: 0,
  three_made: 0, three_attempted: 0,
  ft_made: 0, ft_attempted: 0,
  rating: "", observation: "",
};

export default function MatchStatsDialog({ event, open, onClose }) {
  const [players, setPlayers] = useState([]);
  const [stats, setStats] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState(null);
  const [expanded, setExpanded] = useState({}); // { player_id: bool }

  const load = async () => {
    if (!event) return;
    const cat = event.category && event.category !== "ALL" ? event.category : null;
    const [pRes, sRes] = await Promise.all([
      api.get(`/players${cat ? `?category=${cat}` : ""}`),
      api.get(`/calendar/${event.id}/stats`),
    ]);
    setPlayers(pRes.data || []);
    setStats(sRes.data || []);
    const d = {};
    (sRes.data || []).forEach((s) => {
      d[s.player_id] = { ...DEFAULT_DRAFT, ...s, rating: s.rating ?? "", observation: s.observation ?? "" };
    });
    setDrafts(d);
  };
  useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [event?.id, open]);

  const totals = useMemo(() => {
    const t = { points: 0, assists: 0, rebounds: 0, steals: 0, blocks: 0, fouls: 0, turnovers: 0 };
    stats.forEach((s) => { if (s.status !== "absent") Object.keys(t).forEach((k) => t[k] += s[k] || 0); });
    return t;
  }, [stats]);

  const getDraft = (pid) => drafts[pid] || { ...DEFAULT_DRAFT };
  const setVal = (pid, k, v) => {
    setDrafts((d) => ({
      ...d,
      [pid]: { ...DEFAULT_DRAFT, ...(d[pid] || {}), [k]: v },
    }));
  };
  const setNum = (pid, k, v) => setVal(pid, k, v === "" ? "" : Number(v));

  const save = async (pid) => {
    const d = getDraft(pid);
    const payload = {
      player_id: pid,
      status: d.status || "titulaire",
      minutes_played: Number(d.minutes_played || 0),
      points: Number(d.points || 0),
      assists: Number(d.assists || 0),
      rebounds: Number(d.rebounds || 0),
      steals: Number(d.steals || 0),
      blocks: Number(d.blocks || 0),
      fouls: Number(d.fouls || 0),
      turnovers: Number(d.turnovers || 0),
      fg_made: Number(d.fg_made || 0),
      fg_attempted: Number(d.fg_attempted || 0),
      three_made: Number(d.three_made || 0),
      three_attempted: Number(d.three_attempted || 0),
      ft_made: Number(d.ft_made || 0),
      ft_attempted: Number(d.ft_attempted || 0),
      rating: d.rating === "" || d.rating == null ? null : Number(d.rating),
      observation: d.observation || null,
    };
    setSaving(pid);
    try {
      await api.post(`/calendar/${event.id}/stats`, payload);
      toast.success("Performance enregistrée");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(null);
    }
  };

  const remove = async (sid) => {
    if (!window.confirm("Supprimer cette performance ?")) return;
    await api.delete(`/calendar/${event.id}/stats/${sid}`);
    load();
  };

  if (!event) return null;
  const statById = Object.fromEntries(stats.map((s) => [s.player_id, s]));

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[92vh] overflow-y-auto p-0" data-testid="match-stats-dialog">
        <div className="px-8 py-6 bg-gradient-to-r from-emerald-50 to-white border-b border-slate-200 sticky top-0 z-10">
          <div className="flex items-center gap-4">
            <div className="h-12 w-12 rounded-md bg-emerald-100 border border-emerald-200 flex items-center justify-center">
              <Trophy className="h-6 w-6 text-emerald-700" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-emerald-600">
                Feuille de match • {event.category}
              </div>
              <DialogHeader>
                <DialogTitle className="font-barlow font-bold text-2xl tracking-tight">
                  AMAL VBC vs {event.opponent || "—"}
                </DialogTitle>
              </DialogHeader>
              <div className="text-xs text-slate-600 mt-1">
                {event.date} {event.start_time && `• ${event.start_time}`}
                {event.location && ` • ${event.location}`}
              </div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-wider text-slate-500">Score d'équipe</div>
              <div className="font-barlow font-bold text-3xl text-emerald-700">
                {totals.points}<span className="text-slate-300 mx-1 text-base">pts</span>
              </div>
            </div>
          </div>
        </div>

        <div className="px-6 py-4">
          {players.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              Aucune joueuse trouvée pour cette catégorie.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200">
                <tr className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                  <th className="p-2 w-8"></th>
                  <th className="text-left p-2">Joueuse</th>
                  <th className="text-left p-2 w-32">Statut</th>
                  {BASIC_COLS.map((c) => (
                    <th key={c.key} className="text-center p-2 w-14">{c.label}</th>
                  ))}
                  <th className="text-center p-2 w-16">Note/10</th>
                  <th className="w-24 p-2"></th>
                </tr>
              </thead>
              <tbody>
                {players.map((p) => {
                  const d = getDraft(p.id);
                  const saved = statById[p.id];
                  const isExp = expanded[p.id];
                  const statusInfo = STATUS_OPTIONS.find((s) => s.value === d.status) || STATUS_OPTIONS[0];
                  const isAbsent = d.status === "absent" || d.status === "blesse";
                  return (
                    <Fragment key={p.id}>
                      <tr data-testid={`stat-row-${p.id}`}
                        className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="p-1 text-center">
                          <button
                            onClick={() => setExpanded((e) => ({ ...e, [p.id]: !e[p.id] }))}
                            data-testid={`expand-${p.id}`}
                            className="h-6 w-6 rounded hover:bg-slate-100 inline-flex items-center justify-center text-slate-500"
                            type="button"
                          >
                            {isExp ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                          </button>
                        </td>
                        <td className="p-2">
                          <div className="flex items-center gap-2">
                            <div className="h-8 w-8 rounded-md bg-emerald-50 border border-emerald-200 flex items-center justify-center overflow-hidden flex-shrink-0">
                              {p.photo_url ? (
                                <img src={toAuthUrl(p.photo_url)} alt="" className="h-full w-full object-cover" />
                              ) : <User className="h-4 w-4 text-emerald-600" />}
                            </div>
                            <div className="min-w-0">
                              <Link to={`/joueurs/${p.id}/bilan`} className="font-semibold text-slate-900 leading-tight truncate hover:text-emerald-700 inline-flex items-center gap-1">
                                {p.first_name} {p.last_name}
                                <ExternalLink className="h-3 w-3 opacity-50" />
                              </Link>
                              <div className="text-[10px] text-slate-500">
                                #{p.jersey_number ?? "—"} • {p.position || "—"}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="p-1">
                          <Select value={d.status} onValueChange={(v) => setVal(p.id, "status", v)}>
                            <SelectTrigger className={`h-8 text-xs ${statusInfo.color} border-0`} data-testid={`status-${p.id}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {STATUS_OPTIONS.map((s) => (
                                <SelectItem key={s.value} value={s.value} className="text-xs">{s.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </td>
                        {BASIC_COLS.map((c) => (
                          <td key={c.key} className="p-1 text-center">
                            <Input
                              type="number"
                              min={0}
                              value={isAbsent ? "" : (d[c.key] ?? "")}
                              disabled={isAbsent}
                              onChange={(e) => setNum(p.id, c.key, e.target.value)}
                              data-testid={`stat-${c.key}-${p.id}`}
                              className="h-8 w-12 text-center px-1 text-sm"
                            />
                          </td>
                        ))}
                        <td className="p-1 text-center">
                          <Input
                            type="number" step={0.5} min={0} max={10}
                            value={isAbsent ? "" : (d.rating ?? "")}
                            disabled={isAbsent}
                            onChange={(e) => setNum(p.id, "rating", e.target.value)}
                            data-testid={`rating-${p.id}`}
                            className="h-8 w-14 text-center px-1 text-sm"
                          />
                        </td>
                        <td className="p-1 text-right">
                          <div className="flex gap-1 justify-end">
                            <Button size="sm"
                              onClick={() => save(p.id)}
                              disabled={saving === p.id}
                              data-testid={`save-stat-${p.id}`}
                              className="bg-emerald-600 hover:bg-emerald-700 h-8 px-2">
                              <Save className="h-3.5 w-3.5" />
                            </Button>
                            {saved && (
                              <Button size="sm" variant="ghost"
                                onClick={() => remove(saved.id)}
                                data-testid={`delete-stat-${p.id}`}
                                className="text-red-600 hover:bg-red-50 h-8 w-8 p-0">
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                      {isExp && !isAbsent && (
                        <tr className="bg-slate-50 border-b border-slate-100">
                          <td></td>
                          <td colSpan={12} className="p-3">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div className="space-y-2">
                                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                                  Statistiques de tir
                                </div>
                                <div className="grid grid-cols-3 gap-2">
                                  <ShotInput label="Tirs (FG)" pid={p.id} mk="fg_made" ak="fg_attempted" d={d} setNum={setNum} />
                                  <ShotInput label="3 points" pid={p.id} mk="three_made" ak="three_attempted" d={d} setNum={setNum} />
                                  <ShotInput label="Lancers" pid={p.id} mk="ft_made" ak="ft_attempted" d={d} setNum={setNum} />
                                </div>
                              </div>
                              <div className="space-y-2">
                                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                                  Observation du coach
                                </div>
                                <textarea
                                  rows={3}
                                  value={d.observation || ""}
                                  onChange={(e) => setVal(p.id, "observation", e.target.value)}
                                  data-testid={`observation-${p.id}`}
                                  className="w-full text-sm border border-slate-200 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500"
                                  placeholder="Points forts / axes d'amélioration / attitude…"
                                />
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
              {stats.length > 0 && (
                <tfoot className="border-t-2 border-slate-200 bg-slate-50 font-semibold">
                  <tr>
                    <td></td>
                    <td className="p-2 text-[10px] uppercase tracking-wider text-slate-500">Totaux équipe</td>
                    <td></td>
                    <td className="p-2 text-center text-slate-400">—</td>
                    <td className="p-2 text-center text-emerald-700 font-bold" data-testid="total-points">{totals.points}</td>
                    <td className="p-2 text-center">{totals.assists}</td>
                    <td className="p-2 text-center">{totals.rebounds}</td>
                    <td className="p-2 text-center">{totals.steals}</td>
                    <td className="p-2 text-center">{totals.blocks}</td>
                    <td className="p-2 text-center">{totals.fouls}</td>
                    <td className="p-2 text-center">{totals.turnovers}</td>
                    <td></td>
                    <td></td>
                  </tr>
                </tfoot>
              )}
            </table>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ShotInput({ label, pid, mk, ak, d, setNum }) {
  const made = Number(d[mk] || 0);
  const att = Number(d[ak] || 0);
  const pct = att > 0 ? Math.round((100 * made) / att) : null;
  return (
    <div>
      <div className="text-xs text-slate-600 mb-1 font-medium">{label} <span className="text-slate-400">{pct !== null && `(${pct}%)`}</span></div>
      <div className="flex items-center gap-1">
        <Input type="number" min={0} value={d[mk] ?? ""}
          onChange={(e) => setNum(pid, mk, e.target.value)}
          data-testid={`${mk}-${pid}`}
          className="h-8 text-sm text-center" placeholder="0" />
        <span className="text-slate-400">/</span>
        <Input type="number" min={0} value={d[ak] ?? ""}
          onChange={(e) => setNum(pid, ak, e.target.value)}
          data-testid={`${ak}-${pid}`}
          className="h-8 text-sm text-center" placeholder="0" />
      </div>
    </div>
  );
}
